import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PagingParametersModel } from '@app/core/models/shared/paging-parameters.model';
import { InvoiceDetailModel } from '@app/core/models/invoices/invoice-detail.model';
import { InvoiceModel } from '@app/core/models/invoices/invoice.model';
import { ConfigService } from '@app/core/config/config.service';
import { AuthService } from '@app/core/guards/auth.service';
import { HttpService } from '@app/core/http/http.service';
import { EmailModel } from '@app/core/models/emails/email.model';
import { InvoiceSearchCriteriaModel } from '@app/core/models/invoices/invoice-search-criteria.model';
import { InvoiceTraceabilityModel } from '@app/core/models/invoices/traceability/invoice-traceability.model';
import { InvoiceVoucherProcessModel } from '@app/core/models/invoices/vouchers/invoice-voucher-process.model';
import { WorkflowStageValidationModel } from '@app/core/models/workflows/workflow-stage-validation.model';
import { InvoiceVoucherDetail } from '@app/core/models/invoices/vouchers/invoice-voucher-detail.model';
import { LateInvoiceModel } from '@app/core/models/invoices/invoice-late.model';
import { LateInvoiceGroupModel } from '@app/core/models/invoices/invoice-late-group.model';
import { PagedResult } from '../models/paged-result.model';
import { InvoiceInjectionModel } from '../models/invoices/injection/invoice-injection.model';
import { InvoiceForwardModel } from '../models/invoices/invoice-forward.model';
import { InvoiceComplaint } from '../models/invoices/invoice-complaint.model';
import { InvoicePPRLogModel } from '../models/invoices/invoice-pprlog.model';

@Injectable({
  providedIn: 'root',
})
export class InvoiceService extends HttpService {
  constructor(
    protected http: HttpClient,
    protected config: ConfigService,
    protected authService: AuthService
  ) {
    super(http, config, authService, 'invoices');
  }

  canView(invoiceId: string): Observable<any> {
    return this.invokeGet<any>(`${invoiceId}/canView`);
  }

  getRejectedInvoices(groupsId?: number[]): Observable<InvoiceModel[]> {
    if (!groupsId) {
      return this.invokeGet<InvoiceModel[]>('rejected', '');
    }
    return this.invokeGet<InvoiceModel[]>('rejected', { groupsId: groupsId });
  }

  getDispatchedInvoices(): Observable<InvoiceModel[]> {
    const invoices = this.invokeGet<InvoiceModel[]>(`dispatched`);
    return invoices;
  }

  getUncheckedInvoices(): Observable<InvoiceModel[]> {
    const invoices = this.invokeGet<InvoiceModel[]>(`unchecked`);
    return invoices;
  }

  getWaitingInvoices(): Observable<InvoiceModel[]> {
    const invoices = this.invokeGet<InvoiceModel[]>(`waiting`);
    return invoices;
  }

  getProcessedInvoices(): Observable<InvoiceModel[]> {
    const invoices = this.invokeGet<InvoiceModel[]>(`processed`);
    return invoices;
  }

  getProcessedPromotionInvoices(): Observable<InvoiceModel[]> {
    const invoices = this.invokeGet<InvoiceModel[]>(`processedPromotion`);
    return invoices;
  }

  getAssignedInvoices(groupId: number): Observable<InvoiceModel[]> {
    const invoices = this.invokeGet<InvoiceModel[]>(
      `assigned/groups/${groupId}`
    );
    return invoices;
  }

  getLateGroupsInvoices(
    delayInDays: number,
    isReverseFactoring?: boolean | null,
    isWorkingSituation?: boolean | null
  ): Observable<LateInvoiceGroupModel[]> {
    const invoices = this.invokeGet<LateInvoiceGroupModel[]>(
      `late/groups/${delayInDays}`,
      { isReverseFactoring, isWorkingSituation }
    );
    return invoices;
  }

  getLateInvoices(
    groupId: number,
    delayInDays: number,
    isReverseFactoring?: boolean | null,
    isWorkingSituation?: boolean | null
  ): Observable<LateInvoiceModel[]> {
    const invoices = this.invokeGet<LateInvoiceModel[]>(
      `late/${groupId}/${delayInDays}`,
      { isReverseFactoring, isWorkingSituation }
    );
    return invoices;
  }

  getApprovedVouchers(groupsId?: number[]): Observable<InvoiceModel[]> {
    if (!groupsId) {
      return this.invokeGet<InvoiceModel[]>('voucher/approved', '');
    }
    return this.invokeGet<InvoiceModel[]>('voucher/approved', {
      groupsId: groupsId,
    });
  }

  getInvoiceById(id: string): Observable<InvoiceDetailModel> {
    return this.getById<InvoiceDetailModel>(id);
  }

  getInvoiceTraceability(id: string): Observable<InvoiceTraceabilityModel> {
    const invoiceTraceability = this.invokeGet<InvoiceTraceabilityModel>(
      `${id}/traceability`
    );
    return invoiceTraceability;
  }

  getErrorsPPRById(id: string): Observable<InvoicePPRLogModel[]>{
    const invoicepprlogerrors = this.invokeGet<InvoicePPRLogModel[]>(
      `${id}/pprerrors`
    );
    return invoicepprlogerrors;
  }

  searchInvoices(
    sort?: string,
    order?: string,
    page?: number,
    size?: number,
    invoiceSearchCriteria?: InvoiceSearchCriteriaModel
  ): Observable<PagedResult<InvoiceModel>> {
    const pagingParameters = new PagingParametersModel(
      sort,
      order,
      page + 1,
      size
    );
    const invoicesDataSource = this.invokeGet<PagedResult<InvoiceModel>>(
      'search',
      invoiceSearchCriteria,
      pagingParameters
    );
    return invoicesDataSource;
  }

  updateInvoice(
    invoiceId: string,
    invoiceDetailModel: InvoiceDetailModel
  ): Observable<any> {
    return this.invokePut(invoiceDetailModel, `${invoiceId}`);
  }

  checkInvoice(invoiceId: string): Observable<any> {
    return this.invokePut(null, `${invoiceId}/check`);
  }

  updateCheckInvoice(
    invoiceId: string,
    invoiceDetailModel: InvoiceDetailModel
  ): Observable<any> {
    return this.invokePut(invoiceDetailModel, `${invoiceId}/update-check`);
  }

  checkInvoices(invoicesId: string[]): Observable<any> {
    return this.invokePut(null, `check`, { invoiceId: invoicesId });
  }

  uncheckInvoice(invoiceId: string): Observable<any> {
    return this.invokePut(null, `${invoiceId}/uncheck`);
  }

  forwardInvoice(
    invoiceId: string,
    forwardRequest: InvoiceForwardModel
  ): Observable<any> {
    return this.invokePut(forwardRequest, `${invoiceId}/forward`);
  }

  cancelInvoice(invoiceId: string, email: EmailModel): Observable<any> {
    return this.invokePut(email, `${invoiceId}/cancel`);
  }

  sendBackInvoice(invoiceId: string, email: EmailModel): Observable<any> {
    return this.invokePut(email, `${invoiceId}/send-back`);
  }

  archiveInvoice(invoiceId: string): Observable<any> {
    return this.invokePut(null, `${invoiceId}/archive`);
  }

  resetInvoice(invoiceId: string): Observable<any> {
    return this.invokePut(null, `${invoiceId}/reset`);
  }

  processInvoice(invoiceId: string): Observable<any> {
    return this.invokePut(null, `${invoiceId}/process`);
  }

  updateProcessInvoice(
    invoiceId: string,
    invoiceDetailModel: InvoiceDetailModel
  ): Observable<any> {
    return this.invokePut(invoiceDetailModel, `${invoiceId}/update-process`);
  }

  updateInvoiceSupplier(
    invoiceId: string,
    invoiceDetailModel: InvoiceDetailModel
  ): Observable<any> {
    return this.invokePut(
      invoiceDetailModel,
      `${invoiceId}/update-invoiceupplier`
    );
  }

  processInvoices(invoicesId: string[]): Observable<any> {
    return this.invokePut(null, `process`, { invoiceId: invoicesId });
  }

  canCancelInvoiceProcess(invoiceId: string): Observable<any> {
    return this.invokeGet(`${invoiceId}/cancelprocess`);
  }

  canCancelVoucherProcess(invoiceId: string): Observable<any> {
    return this.invokeGet(`${invoiceId}/voucher/cancelprocess`);
  }

  cancelInvoiceProcess(invoiceId: string): Observable<any> {
    return this.invokePut(null, `${invoiceId}/cancelprocess`);
  }

  cancelInvoiceProcessWithReason(
    invoiceId: string,
    invoicecomplaint: InvoiceComplaint
  ): Observable<any> {
    return this.invokePut(
      invoicecomplaint,
      `${invoiceId}/cancelprocesswithreason`
    );
  }

  cancelInvoiceProcessPromotion(invoiceId: string): Observable<any> {
    return this.invokePut(null, `${invoiceId}/cancelprocesspromotion`);
  }

  attachInvoice(invoiceId: string, file: File): Observable<boolean> {
    return this.invokeUploadFile(file, null, `${invoiceId}/attachment`);
  }

  updateVoucher(
    invoiceDetailModel: InvoiceVoucherDetail,
    invoiceId: string
  ): Observable<any> {
    return this.invokePut(invoiceDetailModel, `voucher/${invoiceId}`);
  }

  processVoucher(
    invoiceId: string,
    invoiceVoucherProcessModel: InvoiceVoucherProcessModel
  ): Observable<any> {
    return this.invokePut(
      invoiceVoucherProcessModel,
      `voucher/${invoiceId}/process`
    );
  }

  validateVoucher(invoiceId: string): Observable<any> {
    return this.invokePut(null, `voucher/${invoiceId}/validate`);
  }

  updateValidateVoucher(
    invoiceId: string,
    invoiceDetailModel: InvoiceDetailModel
  ): Observable<any> {
    return this.invokePut(
      invoiceDetailModel,
      `voucher/${invoiceId}/update-validate`
    );
  }

  validateVouchers(invoicesId: string[]): Observable<any> {
    return this.invokePut(null, `voucher/validate`, { invoiceId: invoicesId });
  }

  pickInvoice(invoiceId: string): Observable<any> {
    return this.invokePut(null, `${invoiceId}/pick`);
  }

  unpickInvoice(invoiceId: string): Observable<any> {
    return this.invokePut(null, `${invoiceId}/unpick`);
  }

  complaintInvoice(
    invoiceId: string,
    invoicecomplaint: InvoiceComplaint
  ): Observable<any> {
    return this.invokePut(invoicecomplaint, `${invoiceId}/complaint`);
  }

  uncomplaintInvoice(invoiceId: string): Observable<any> {
    return this.invokePut(null, `${invoiceId}/uncomplaint`);
  }

  returnOperationnalInvoice(
    invoiceId: string,
    invoicecomplaint: InvoiceComplaint
  ): Observable<any> {
    return this.invokePut(invoicecomplaint, `${invoiceId}/returnOperationnal`);
  }

  // Workflow classique
  approveCurrentWorkflowStage(
    invoiceId: string,
    workflowStageValidationModel: WorkflowStageValidationModel
  ): Observable<any> {
    return this.invokePut(
      workflowStageValidationModel,
      `voucher/${invoiceId}/approve`
    );
  }

  approveCurrentWorkflowsStage(
    invoicesId: string[],
    workflowStageValidationModel: WorkflowStageValidationModel
  ): Observable<any> {
    return this.invokePut(workflowStageValidationModel, `voucher/approve`, {
      invoiceId: invoicesId,
    });
  }

  // Workflow Promotion
  approveCurrentWorkflowStagePromotion(
    invoiceId: string,
    workflowStageValidationModel: WorkflowStageValidationModel
  ): Observable<any> {
    return this.invokePut(
      workflowStageValidationModel,
      `promotion/${invoiceId}/approve`
    );
  }

  getAttachment(invoiceId: string, attachmentId: number): Observable<Blob> {
    return this.invokeGetFile(`${invoiceId}/attachment/${attachmentId}`);
  }

  getAttachmentVoucher(invoiceId: string): Observable<Blob> {
    return this.invokeGetFile(`${invoiceId}/attachment/voucher`);
  }

  getAttachmentPromotion(invoiceId: string): Observable<Blob> {
    return this.invokeGetFile(`${invoiceId}/attachment/promotion`);
  }

  removeAttachment(invoiceId: string, attachmentId: number): Observable<Blob> {
    return this.invokeDelete(`${invoiceId}/attachment/${attachmentId}`);
  }

  getInvoicesAttachment(
    invoicesId: string[],
    requestId: string
  ): Observable<Blob> {
    return this.invokeGetMultipleFiles(
      `attachments/invoices/${requestId}`,
      invoicesId
    );
  }

  getAttachmentVouchers(
    invoicesId: string[],
    requestId: string
  ): Observable<Blob> {
    return this.invokeGetMultipleFiles(
      `attachments/vouchers/${requestId}`,
      invoicesId
    );
  }

  getAttachmentPromotions(
    invoicesId: string[],
    requestId: string
  ): Observable<Blob> {
    return this.invokeGetMultipleFiles(
      `attachments/promotions/${requestId}`,
      invoicesId
    );
  }

  injectInvoice(invoice: InvoiceInjectionModel): Observable<any> {
    return this.invokePost(invoice, 'inject');
  }

  manualInvoice(invoice: InvoiceDetailModel): Observable<any> {
    return this.invokePost(invoice, 'manual');
  }

  sendToWaitingStatus(invoiceId: string): Observable<any> {
    return this.invokePut(null, `${invoiceId}/waitingStatus`);
  }

  removeFromWaitingStatus(invoiceId: string): Observable<any> {
    return this.invokePut(null, `${invoiceId}/waitingStatuscreated`);
  }

  resetWaitingStatus(invoiceId: string): Observable<any> {
    return this.invokePut(null, `${invoiceId}/resetwaitingStatus`);
  }

  sendInvoicePPR(invoiceId: string): Observable<any> {
    return this.invokePut(null, `${invoiceId}/sendInvoicePPR`);
  }

  reSendInvoicePPR(invoiceId: string): Observable<any> {
    return this.invokePut(null, `${invoiceId}/reSendInvoicePPR`);
  }

  getCumulativePreviousSituationAmount(
    marketId: string
  ): Observable<any> {
    return this.invokeGet(`cumulSituation/${marketId}`);
  }

  getWorkCumulAmount(marketId: string): Observable<any> {
    return this.invokeGet(`workcumulht/${marketId}`);
  }
}
